// Client-side index of pages searchable via global search.
// The backend /api/user/search returns only entities — this index covers
// named pages (agendas, reports, codebooks) that have no entity to match.
// Mirrors the sidebar menu structure in Layout.tsx — keep permissions in sync.
// Pages needing owner/period context link to their landing/selector route.

import type { SearchResult } from '../components/GlobalSearchModal';

export type SearchablePage = {
  url: string;
  /** i18n key for the page title — reuse existing menu keys */
  labelKey: string;
  /** required permission — array means ANY of them; undefined = everyone */
  permission?: string | string[];
  /** ADMIN role only (menu item has no permission, just a role check) */
  adminOnly?: boolean;
  icon: string;
  /** extra search terms — matched diacritic-insensitively */
  keywords: string[];
};

export const SEARCHABLE_PAGES: SearchablePage[] = [
  // ---------------------------------------------------------------- Obchod
  {
    url: '/accounts',
    labelKey: 'adminAccountsPohoda',
    permission: 'pohoda.accounts.view',
    icon: '🏢',
    keywords: [
      'účty', 'ucty', 'klienti', 'firmy', 'zákazníci', 'zakaznici', 'adresář',
      'adresar', 'odběratelé', 'odberatele', 'accounts', 'customers', 'clients',
      'kunden', 'adresář pohoda', 'crm karty',
    ],
  },
  {
    url: '/contacts',
    labelKey: 'menuContacts',
    permission: 'obchod.contacts.view',
    icon: '👥',
    keywords: [
      'kontakty', 'osoby', 'contact', 'contacts', 'person', 'people',
      'kontakte', 'ansprechpartner',
    ],
  },
  {
    url: '/obchod/invoices',
    labelKey: 'adminInvoicesPohoda',
    permission: 'obchod.invoices.view',
    icon: '🧾',
    keywords: [
      'faktury', 'fakturace', 'fakturace', 'vydané faktury', 'faktura',
      'invoice', 'invoices', 'rechnung', 'rechnungen', 'faktur', 'inv',
    ],
  },
  {
    url: '/obchod/orders',
    labelKey: 'adminOrdersPohoda',
    permission: 'obchod.orders.view',
    icon: '📑',
    keywords: [
      'objednávky', 'objednavky', 'objednávka', 'orders', 'order',
      'bestellung', 'bestellungen',
    ],
  },
  {
    url: '/obchod/orders/status-legend',
    labelKey: 'menuLogisticsStatuses',
    permission: 'obchod.orders.view',
    icon: '🚚',
    keywords: [
      'stavy objednávek', 'logistika', 'expedice', 'doprava', 'stav',
      'status legend', 'lieferstatus',
    ],
  },
  {
    url: '/obchod/shipments',
    labelKey: 'menuShipments',
    permission: 'obchod.orders.view',
    icon: '📮',
    keywords: [
      'zásilky', 'zasilky', 'zásilka', 'expedice', 'doprava', 'balík', 'baliky',
      'shipments', 'shipment', 'parcels', 'sendung', 'packeta', 'ppl',
    ],
  },
  {
    url: '/obchod/exchange-rates',
    labelKey: 'menuExchangeRates',
    permission: 'obchod.products.view',
    icon: '💱',
    keywords: [
      'kurzovní lístek', 'kurzovni listek', 'kurzy', 'kurz', 'měna', 'mena',
      'exchange rates', 'currency', 'eur', 'usd', 'wechselkurs', 'kursliste',
    ],
  },
  {
    url: '/datovka',
    labelKey: 'menuDatovka',
    permission: 'databox.view',
    icon: '📬',
    keywords: [
      'datová schránka', 'datova schranka', 'datovka', 'isds', 'zfo',
      'data box', 'databox', 'datové zprávy', 'datove zpravy',
    ],
  },
  {
    url: '/obchod/purchase-orders',
    labelKey: 'menuPurchaseOrders',
    permission: 'obchod.purchase-orders.view',
    icon: '🛒',
    keywords: [
      'nákupní objednávky', 'nakupni objednavky', 'nákup', 'nakup',
      'purchase orders', 'einkauf', 'bestellung',
    ],
  },
  {
    url: '/obchod/products',
    labelKey: 'adminProductsPohoda',
    permission: 'obchod.products.view',
    icon: '📦',
    keywords: [
      'produkty', 'zboží', 'zbozi', 'katalog', 'products', 'waren',
      'artikel', 'sortiment',
    ],
  },
  {
    url: '/obchod/sklad',
    labelKey: 'menuSklad',
    permission: 'obchod.products.view',
    icon: '🏬',
    keywords: [
      'sklad', 'zásoby', 'zasoby', 'skladové zásoby', 'warehouse', 'stock',
      'lager', 'bestand',
    ],
  },
  {
    url: '/obchod/price-actions',
    labelKey: 'menuPriceActions',
    permission: 'obchod.products.view',
    icon: '🏷️',
    keywords: [
      'akční ceny', 'akcni ceny', 'slevy', 'sleva', 'akce', 'price actions',
      'discounts', 'rabatt', 'aktionspreise',
    ],
  },
  {
    url: '/obchod/brands',
    labelKey: 'menuBrandsPohoda',
    permission: 'obchod.brands.view',
    icon: '🏷️',
    keywords: ['značky', 'znacky', 'brands', 'marken', 'výrobci', 'vyrobci'],
  },
  {
    url: '/brands/analytics',
    labelKey: 'menuBrandAnalytics',
    permission: 'obchod.analytics.view',
    icon: '📈',
    keywords: [
      'analytika značek', 'brand analytics', 'statistiky značek', 'markenanalyse',
    ],
  },
  {
    url: '/obchod/analytics',
    labelKey: 'menuSalesAnalytics',
    permission: 'obchod.analytics.view',
    icon: '📈',
    keywords: [
      'analytika', 'statistiky', 'prodeje', 'tržby', 'obrat', 'analytics',
      'sales', 'auswertung', 'statistik', 'reporting', 'aging',
    ],
  },
  // ---------------------------------------------------------------- Energetika
  {
    url: '/energetika/spot-ceny',
    labelKey: 'menuSpotPrices',
    permission: 'energetika.spot-prices.view',
    icon: '⚡',
    keywords: [
      'spotové ceny', 'spotove ceny', 'elektřina', 'elektrina', 'energie',
      'ote', 'dam', 'denní trh', 'denni trh', 'ida', 'odchylky',
      'spot prices', 'electricity', 'strompreis', 'energetika',
    ],
  },
  // ---------------------------------------------------------------- CRM
  {
    url: '/crm',
    labelKey: 'menuCrmDashboard',
    permission: 'crm.view',
    icon: '🎯',
    keywords: ['crm', 'dashboard', 'přehled', 'prehled', 'obchod'],
  },
  {
    url: '/crm/leads',
    labelKey: 'menuCrmLeads',
    permission: 'crm.view',
    icon: '🚀',
    keywords: [
      'leady', 'lead', 'leads', 'poptávky', 'poptavky', 'potenciální klienti',
      'akvizice', 'interessenten',
    ],
  },
  {
    url: '/crm/cases',
    labelKey: 'menuCrmCases',
    permission: 'crm.view',
    icon: '📋',
    keywords: [
      'případy', 'pripady', 'cases', 'case', 'servisní případy', 'reklamace',
      'servis', 'support', 'ticket', 'tickets', 'vorgänge',
    ],
  },
  {
    url: '/crm/opportunities',
    labelKey: 'menuCrmOpportunities',
    permission: 'crm.view',
    icon: '💡',
    keywords: [
      'příležitosti', 'prilezitosti', 'opportunities', 'obchodní příležitosti',
      'pipeline', 'zakázky', 'opportunitäten',
    ],
  },
  {
    url: '/crm/activities',
    labelKey: 'menuCrmActivities',
    permission: 'calendar.view',
    icon: '🗓️',
    keywords: [
      'aktivity', 'činnosti', 'cinnosti', 'activities', 'aktivitäten',
      'historie aktivit',
    ],
  },
  {
    url: '/crm/supervision',
    labelKey: 'menuCrmSupervision',
    permission: 'crm.supervise',
    icon: '🛡️',
    keywords: ['dohled', 'supervision', 'supervize', 'kontrola obchodu'],
  },
  // ---------------------------------------------------------------- Kalendář
  {
    url: '/calendar',
    labelKey: 'calendar',
    permission: 'calendar.view',
    icon: '📅',
    keywords: [
      'kalendář', 'kalendar', 'calendar', 'kalender', 'události', 'udalosti',
      'termíny', 'terminy', 'schůzky', 'schuzky',
    ],
  },
  {
    url: '/tasks',
    labelKey: 'tasks',
    permission: 'calendar.view',
    icon: '✅',
    keywords: [
      'úkoly', 'ukoly', 'úkol', 'tasks', 'task', 'todo', 'to do',
      'úkolovník', 'aufgaben',
    ],
  },
  {
    url: '/meetings',
    labelKey: 'meetings',
    permission: 'calendar.view',
    icon: '🤝',
    keywords: [
      'schůzky', 'schuzky', 'jednání', 'jednani', 'porady', 'porada',
      'meetings', 'meeting', 'besprechung', 'termin',
    ],
  },
  {
    url: '/calls',
    labelKey: 'calls',
    permission: 'calendar.view',
    icon: '📞',
    keywords: [
      'hovory', 'telefonáty', 'telefonaty', 'calls', 'telefon', 'call',
      'anrufe', 'volání', 'volani',
    ],
  },
  // ---------------------------------------------------------------- Komunikace
  {
    url: '/email',
    labelKey: 'email',
    permission: 'email.view',
    icon: '✉️',
    keywords: [
      'e-mail', 'email', 'mail', 'pošta', 'posta', 'schránka', 'schranka',
      'inbox', 'zprávy', 'zpravy',
    ],
  },
  {
    url: '/sms',
    labelKey: 'menuSms',
    permission: 'sms.view',
    icon: '💬',
    keywords: ['sms', 'textovky', 'textovka', 'zprávy sms', 'text message'],
  },
  {
    url: '/in-chat',
    labelKey: 'menuInChat',
    icon: '💬',
    keywords: ['chat', 'in-chat', 'inchat', 'interní chat', 'zprávy'],
  },
  {
    url: '/documents',
    labelKey: 'menuDocuments',
    permission: 'documents.view',
    icon: '📁',
    keywords: ['dokumenty', 'documents', 'soubory', 'files', 'přílohy', 'smlouva', 'contract'],
  },
  {
    url: '/chat',
    labelKey: 'menuChat',
    permission: 'chat.view',
    icon: '💬',
    keywords: ['chat', 'webchat', 'web chat', 'chatování', 'chatovani'],
  },
  // ---------------------------------------------------------------- Platby
  {
    url: '/devices',
    labelKey: 'terminals',
    permission: 'stripe.terminals.view',
    icon: '💳',
    keywords: [
      'terminály', 'terminaly', 'platební terminál', 'terminal', 'terminals',
      'karty', 'kartičky', 'stripe',
    ],
  },
  {
    url: '/payments',
    labelKey: 'payments',
    permission: 'stripe.payments-history.view',
    icon: '💳',
    keywords: [
      'platby', 'payments', 'transakce', 'úhrady', 'uhrady', 'zahlungen',
      'payment',
    ],
  },
  {
    url: '/payment-requests',
    labelKey: 'paymentRequests',
    permission: 'stripe.payment-requests.view',
    icon: '💳',
    keywords: [
      'platební požadavky', 'platebni pozadavky', 'žádosti o platbu',
      'payment requests', 'zahlungsaufforderung',
    ],
  },
  {
    url: '/create-payment',
    labelKey: 'quickPayment',
    permission: 'stripe.create-payment.view',
    icon: '💳',
    keywords: [
      'rychlá platba', 'rychla platba', 'nová platba', 'quick payment',
      'vytvořit platbu',
    ],
  },
  {
    url: '/credit',
    labelKey: 'credit',
    permission: 'user.credit.view',
    icon: '💵',
    keywords: ['kredit', 'credit', 'dobití', 'dobiti', 'guthaben'],
  },
  // ---------------------------------------------------------------- Účetnictví
  {
    url: '/armorik',
    labelKey: 'menuArmorikSelectOwner',
    permission: 'armorik.accounting.view',
    icon: '📊',
    keywords: [
      'účetnictví', 'ucetnictvi', 'armorik', 'accounting', 'buchhaltung',
      'účto', 'ucto',
    ],
  },
  {
    url: '/armorik/owners',
    labelKey: 'menuArmorikOwners',
    permission: 'armorik.accounting.view',
    icon: '👤',
    keywords: [
      'majitelé', 'maitele', 'účetní jednotky', 'ucetni jednotky', 'owners',
      'vlastníci', 'vlastnici',
    ],
  },
  {
    url: '/armorik/periods',
    labelKey: 'menuArmorikPeriods',
    permission: 'armorik.accounting.view',
    icon: '📅',
    keywords: [
      'období', 'obdobi', 'účetní období', 'ucetni obdobi', 'periods',
      'period', 'zeiträume',
    ],
  },
  {
    url: '/armorik/vat-rates',
    labelKey: 'menuVatRates',
    permission: 'armorik.accounting.view',
    icon: '％',
    keywords: [
      'sazby dph', 'sazby', 'vat rates', 'dph sazby', 'mwst', 'steuersätze',
      'ust',
    ],
  },
  {
    url: '/armorik/vat-report',
    labelKey: 'menuVatReport',
    permission: 'armorik.accounting.view',
    icon: '📊',
    keywords: [
      'výkazy', 'vykazy', 'dph', 'vat', 'kontrolní hlášení', 'kontrolni hlaseni',
      'přiznání', 'priznani', 'dphkh1', 'dphdp3', 'souhrnné hlášení',
      'souhrnne hlaseni', 'shv', 'daňové přiznání', 'mwst', 'ust', 'report',
    ],
  },
  {
    url: '/armorik/chart-of-accounts',
    labelKey: 'menuChartOfAccounts',
    permission: 'armorik.accounting.view',
    icon: '📒',
    keywords: [
      'účtový rozvrh', 'uctovy rozvrh', 'rozvrh účtů', 'chart of accounts',
      'coa', 'kontenrahmen', 'účtová osnova', 'uctova osnova',
    ],
  },
  {
    url: '/armorik/coa-templates',
    labelKey: 'menuCoaTemplates',
    permission: 'armorik.accounting.view',
    icon: '📋',
    keywords: [
      'šablony rozvrhu', 'sablony rozvrhu', 'coa templates', 'účtové šablony',
    ],
  },
  {
    url: '/armorik/pre-accounting',
    labelKey: 'menuPreAccounting',
    permission: 'armorik.accounting.view',
    icon: '🔗',
    keywords: [
      'předkontace', 'predkontace', 'předúčtování', 'preductovani',
      'pre-accounting', 'vorkontierung',
    ],
  },
  {
    url: '/armorik/folders',
    labelKey: 'menuFolders',
    permission: 'armorik.accounting.view',
    icon: '📁',
    keywords: ['složky', 'slozky', 'folders', 'šanony', 'sanony', 'ordner'],
  },
  {
    url: '/armorik/number-sequences',
    labelKey: 'armorikTileNumberSequences',
    permission: 'armorik.accounting.view',
    icon: '🔢',
    keywords: [
      'číselné řady', 'ciselne rady', 'číselná řada', 'numerace', 'číslování',
      'dokladová řada', 'prefix', 'sequence', 'sequences', 'numbering',
      'nummernkreis', 'nummerierung',
    ],
  },
  {
    url: '/armorik/bank-accounts',
    labelKey: 'menuOurBankAccounts',
    permission: 'armorik.accounting.view',
    icon: '🏦',
    keywords: [
      'bankovní účty', 'bankovni ucty', 'naše účty', 'nase ucty', 'bank accounts',
      'konten', 'bankkonten',
    ],
  },
  {
    url: '/armorik/partner-bank-accounts',
    labelKey: 'menuPartnerBankAccounts',
    permission: 'armorik.accounting.view',
    icon: '🏦',
    keywords: [
      'účty partnerů', 'ucty partneru', 'účty dodavatelů', 'bankovní účty partnerů',
      'partner bank accounts',
    ],
  },
  {
    url: '/armorik/bank-statements',
    labelKey: 'menuBankStatements',
    permission: 'armorik.accounting.view',
    icon: '📃',
    keywords: [
      'výpisy', 'vypisy', 'bankovní výpis', 'bankovni vypis', 'banka',
      'pohyby', 'platby', 'párování', 'parovani', 'transactions', 'payments',
      'reconciliation', 'bankauszug', 'kontoauszug', 'vincis', 'fio', 'import',
    ],
  },
  {
    url: '/armorik/saldo-sheets',
    labelKey: 'menuSaldo',
    permission: 'armorik.saldo.view',
    icon: '📊',
    keywords: ['saldo', 'saldokonto', 'saldo sheets', 'salden'],
  },
  // ---------------------------------------------------------------- Vincis
  {
    url: '/vincis',
    labelKey: 'menuVincisAgenda',
    permission: ['vincis.faktury.view', 'vincis.vykazy.view'],
    icon: '📄',
    keywords: [
      'vincis', 'vincis faktury', 'vykazy fakturace', 'fakturace vincis',
    ],
  },
  // ---------------------------------------------------------------- Mzdy
  {
    url: '/payroll',
    labelKey: 'menuPayroll',
    permission: 'payroll.view',
    icon: '💵',
    keywords: ['mzdy', 'payroll', 'lohn', 'gehalt', 'mzdová agenda'],
  },
  {
    url: '/payroll/employees',
    labelKey: 'menuEmployees',
    permission: 'payroll.view',
    icon: '👥',
    keywords: [
      'zaměstnanci', 'zamestnanci', 'employees', 'mitarbeiter', 'personal',
    ],
  },
  {
    url: '/payroll/periods',
    labelKey: 'menuPayrollPeriods',
    permission: 'payroll.view',
    icon: '📅',
    keywords: ['mzdová období', 'mzdova obdobi', 'payroll periods'],
  },
  {
    url: '/payroll/slips',
    labelKey: 'menuPayrollSlips',
    permission: 'payroll.view',
    icon: '💰',
    keywords: [
      'výplatní pásky', 'vyplatni pasky', 'pásky', 'payslips', 'slips',
      'lohnabrechnung',
    ],
  },
  {
    url: '/payroll/components',
    labelKey: 'menuPayrollComponents',
    permission: 'payroll.view',
    icon: '📋',
    keywords: ['mzdové složky', 'mzdove slozky', 'payroll components'],
  },
  {
    url: '/payroll/config',
    labelKey: 'menuPayrollConfig',
    permission: 'payroll.view',
    icon: '⚙️',
    keywords: ['nastavení mezd', 'payroll config', 'konfigurace mezd'],
  },
  // ---------------------------------------------------------------- Ostatní
  {
    url: '/reservations',
    labelKey: 'menuReservations',
    permission: 'reservations.view',
    icon: '📅',
    keywords: [
      'rezervace', 'booking', 'rezervace služeb', 'reservations',
      'buchungen', 'termíny',
    ],
  },
  {
    url: '/reservations/services',
    labelKey: 'menuReservationServices',
    permission: 'reservations.view',
    icon: '🔧',
    keywords: ['rezervační služby', 'rezervacni sluzby', 'služby', 'services'],
  },
  {
    url: '/consumption',
    labelKey: 'menuConsumption',
    permission: 'consumption.view',
    icon: '⚡',
    keywords: [
      'spotřeba', 'spotreba', 'měřidla', 'meridla', 'odečty', 'odecty',
      'energie', 'consumption', 'verbrauch',
    ],
  },
  {
    url: '/monitors',
    labelKey: 'menuMonitors',
    permission: 'monitors.view',
    icon: '📡',
    keywords: ['monitory', 'monitoring', 'watchdog', 'hlídání', 'hlidani'],
  },
  {
    url: '/notes',
    labelKey: 'menuNotes',
    icon: '📝',
    keywords: ['poznámky', 'poznamky', 'notes', 'notizen', 'zápisky', 'zapisky'],
  },
  {
    url: '/news',
    labelKey: 'menuNews',
    icon: '📰',
    keywords: ['novinky', 'aktuality', 'news', 'nachrichten', 'oznámení', 'oznameni'],
  },
  {
    url: '/links',
    labelKey: 'menuLinks',
    icon: '🔗',
    keywords: ['odkazy', 'links', 'linky', 'verlinkungen'],
  },
  {
    url: '/help',
    labelKey: 'menuNapoveda',
    icon: '📚',
    keywords: [
      'nápověda', 'napoveda', 'help', 'návod', 'navod', 'dokumentace',
      'hilfe', 'příručka', 'prirucka', 'faq',
    ],
  },
  // ---------------------------------------------------------------- Admin
  {
    url: '/admin/payments',
    labelKey: 'adminPayments',
    permission: 'stripe.payments.view',
    icon: '🏦',
    keywords: ['platby admin', 'admin payments', 'správa plateb'],
  },
  {
    url: '/admin/stripe-logs',
    labelKey: 'adminStripeLogs',
    permission: 'stripe.payments.view',
    icon: '📜',
    keywords: ['stripe logy', 'stripe logs', 'logy plateb'],
  },
  {
    url: '/admin/credit',
    labelKey: 'adminCreditOverview',
    permission: 'stripe.credit.view',
    icon: '💵',
    keywords: ['kredit přehled', 'credit overview', 'admin kredit'],
  },
  {
    url: '/admin/products',
    labelKey: 'adminProducts',
    permission: 'admin.products.view',
    icon: '🛠️',
    keywords: ['produkty admin', 'admin products', 'správa produktů'],
  },
  {
    url: '/admin/eshop',
    labelKey: 'menuEshop',
    permission: 'eshop.manage',
    icon: '🛒',
    keywords: [
      'eshop', 'e-shop', 'obchod', 'kategorie', 'produkty eshop', 'webshop',
    ],
  },
  {
    url: '/cms/editor',
    labelKey: 'menuWebEditor',
    permission: 'cms.edit',
    icon: '✏️',
    keywords: [
      'editor webu', 'web editor', 'cms', 'redakční systém', 'stránky',
      'stranky', 'webové stránky', 'page editor',
    ],
  },
  {
    url: '/admin/entities',
    labelKey: 'menuEntities',
    adminOnly: true,
    icon: '🗃️',
    keywords: ['entity', 'entita', 'entities', 'datové modely', 'admin entity'],
  },
  {
    url: '/admin/view-configs',
    labelKey: 'menuViewConfigs',
    adminOnly: true,
    icon: '👁️',
    keywords: ['view configs', 'zobrazení', 'konfigurace pohledů'],
  },
  {
    url: '/admin/datatable-filters',
    labelKey: 'menuDatatableFilters',
    adminOnly: true,
    icon: '🔎',
    keywords: ['datatable filtry', 'saved filters', 'uložené filtry'],
  },
  {
    url: '/admin/i18n-modules',
    labelKey: 'i18nModulesTitle',
    adminOnly: true,
    icon: '🌐',
    keywords: ['i18n', 'překlady', 'preklady', 'jazyky', 'translations'],
  },
  {
    url: '/admin/news',
    labelKey: 'adminNewsTitle',
    adminOnly: true,
    icon: '📰',
    keywords: ['novinky admin', 'admin news', 'správa novinek'],
  },
  {
    url: '/admin/backend-health',
    labelKey: 'adminBackendHealthTitle',
    adminOnly: true,
    icon: '📡',
    keywords: ['backend health', 'stav backendu', 'health', 'systém'],
  },
  {
    url: '/admin/pohoda-users',
    labelKey: 'pohodaUsersTitle',
    adminOnly: true,
    icon: '👥',
    keywords: ['pohoda uživatelé', 'pohoda uzivatele', 'mapování uživatelů'],
  },
  {
    url: '/admin/cms/sites',
    labelKey: 'menuCmsSites',
    adminOnly: true,
    icon: '🌐',
    keywords: ['cms weby', 'cms sites', 'weby', 'správa webů'],
  },
  {
    url: '/admin/cms/pages',
    labelKey: 'menuCmsPages',
    adminOnly: true,
    icon: '📄',
    keywords: ['cms stránky', 'cms pages', 'stránky cms'],
  },
  {
    url: '/cms/media',
    labelKey: 'cmsMediaTitle',
    permission: 'cms.edit',
    icon: '🖼️',
    keywords: ['cms assets', 'obrázky cms', 'soubory cms', 'média', 'media'],
  },
  {
    url: '/admin/cms/templates',
    labelKey: 'menuCmsTemplates',
    adminOnly: true,
    icon: '📐',
    keywords: ['cms šablony', 'cms sablony', 'templates', 'šablony stránek'],
  },
  {
    url: '/admin/cms/blocks',
    labelKey: 'menuCmsBlocks',
    adminOnly: true,
    icon: '🧩',
    keywords: ['cms bloky', 'reusable blocks', 'bloky', 'komponenty'],
  },
];

/** Keywords for a menu item — single source of truth in SEARCHABLE_PAGES. */
export function menuSearchKeywords(href: string): string[] {
  return SEARCHABLE_PAGES.find((p) => p.url === href)?.keywords ?? [];
}

/** Diacritic-insensitive lowercase for matching ("výkazy" == "vykazy"). */
export function norm(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function hasAccess(p: SearchablePage, isAdmin: boolean, permissions: string[]): boolean {
  if (p.adminOnly) return isAdmin;
  if (isAdmin) return true;
  if (!p.permission) return true;
  const required = Array.isArray(p.permission) ? p.permission : [p.permission];
  return required.some((perm) => permissions.includes(perm));
}

/**
 * Returns page results matching the query.
 * Matches: localized label (current locale), English label, URL, keywords.
 */
export function searchPages(
  query: string,
  t: (key: string) => string,
  tEn: (key: string) => string,
  role: string | undefined,
  permissions: string[],
): SearchResult[] {
  const q = norm(query);
  if (q.length < 2) return [];
  const isAdmin = role === 'ADMIN';
  return SEARCHABLE_PAGES
    .filter((p) => hasAccess(p, isAdmin, permissions))
    .filter((p) =>
      norm(t(p.labelKey)).includes(q)
      || norm(tEn(p.labelKey)).includes(q)
      || norm(p.url).includes(q)
      || p.keywords.some((k) => norm(k).includes(q)))
    .map((p, i) => ({
      type: 'page',
      id: -(i + 1),
      title: t(p.labelKey),
      subtitle: p.url,
      url: p.url,
      icon: p.icon,
    }));
}
